import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import {
  MdBusiness,
  MdAdd,
  MdDelete,
  MdStar,
  MdCheckCircle,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

export const ShowcaseCompanyList = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal State
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [eligibleCompanies, setEligibleCompanies] = useState<any[]>([]);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [displayOrder, setDisplayOrder] = useState<number>(0);
  const [error, setError] = useState<string>("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.showcaseCompany.getAll();
      if (r?.ok) setData(r.data?.data ?? r.data ?? []);
      else setData([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Showcase Companies",
        items: [{ label: "All Companies Showcase", route: "/dashboard/showcase-companies" }],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = async () => {
    setSelectedCompanyId("");
    setDisplayOrder(0);
    setError("");
    setOpen(true);
    setLoadingEligible(true);

    try {
      const r = await api.showcaseCompany.getEligible();
      if (r?.ok) {
        const list = r.data?.data ?? r.data ?? [];
        setEligibleCompanies(list);
        if (list.length > 0) {
          setSelectedCompanyId(String(list[0].id));
        }
      } else {
        setEligibleCompanies([]);
      }
    } finally {
      setLoadingEligible(false);
    }
  };

  const submit = async () => {
    if (saving) return;
    if (!selectedCompanyId) {
      setError("Please select an eligible company.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const r = await api.showcaseCompany.create({
        companyId: Number(selectedCompanyId),
        displayOrder: Number(displayOrder) || 0,
      });

      if (!r?.ok) {
        throw new Error(r?.message || "Failed to add company to showcase");
      }

      setOpen(false);
      load();
    } catch (err: any) {
      setError(err?.message || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm(`Are you sure you want to remove "${row.companyName || 'this company'}" from showcase?`)) {
      return;
    }

    try {
      const r = await api.showcaseCompany.delete(row.id);
      if (r?.ok) {
        load();
      } else {
        alert(r?.message || "Failed to remove from showcase");
      }
    } catch (err: any) {
      alert(err?.message || "An error occurred");
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "company",
        title: "Company",
        minWidth: 280,
        accessor: (r: any) => r.companyName || r.company?.companyName,
        render: (_: any, v: string) => (
          <Badge
            icon={MdBusiness}
            label={v}
            color="#0EA5E9"
            bg="rgba(14,165,233,.15)"
          />
        ),
      },
      {
        key: "location",
        title: "Location",
        minWidth: 180,
        accessor: (r: any) => `${r.city || r.company?.city || ''}, ${r.country || r.company?.country || ''}`.replace(/^,\s*|,\s*$/g, '') || "-",
      },
      {
        key: "email",
        title: "Email",
        minWidth: 200,
        accessor: (r: any) => r.email || r.company?.email || "-",
      },
      {
        key: "order",
        title: "Display Order",
        minWidth: 120,
        accessor: (r: any) => r.displayOrder ?? 0,
        render: (_: any, v: number) => (
          <span style={{ fontWeight: 600, color: "#475569" }}>{v}</span>
        ),
      },
      {
        key: "status",
        title: "Status",
        minWidth: 120,
        render: () => (
          <Badge
            icon={MdCheckCircle}
            label="In Showcase"
            color="#16A34A"
            bg="rgba(22,163,74,.15)"
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 120,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "delete",
                icon: MdDelete,
                color: "#EF4444",
                title: "Remove from Showcase",
                onClick: () => handleDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [load]
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Showcase Companies (All Companies)</div>
          <div style={S.subTitle}>
            Manage companies displayed on the public "All Companies" page. Only approved & active companies can be selected.
          </div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Add Company to Showcase
        </Button>
      </div>

      <Table
        tableKey="showcase-company-list"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>Add Company to Showcase</div>
        <div style={S.modalDesc}>
          Only verified, active companies whose primary users can log in are shown in this list.
        </div>

        {loadingEligible ? (
          <div style={{ padding: "20px 0", textAlign: "center", color: "#64748B" }}>
            Loading eligible companies...
          </div>
        ) : (
          <div style={S.form}>
            {eligibleCompanies.length === 0 ? (
              <div style={{ padding: 12, background: "#FEF3C7", color: "#92400E", borderRadius: 8, fontSize: 13 }}>
                No eligible companies available to add. Either all verified companies are already in showcase, or pending accounts have not been approved yet.
              </div>
            ) : (
              <div>
                <label style={S.label}>Select Company *</label>
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  style={S.input}
                  disabled={saving}
                >
                  {eligibleCompanies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.companyName} {c.city ? `(${c.city}, ${c.country})` : ""} - User: {c.userFullName || c.username}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={S.label}>Display Order (Lower numbers appear first)</label>
              <input
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(Number(e.target.value))}
                placeholder="0"
                style={S.input}
                disabled={saving}
              />
            </div>

            {error && <div style={S.error}>{error}</div>}
          </div>
        )}

        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={saving || loadingEligible || eligibleCompanies.length === 0}
          >
            Add to Showcase
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: 20, background: "#F8FAFC" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: { fontSize: 13, color: "#64748B", marginTop: 2 },
  modalTitle: { fontSize: 18, fontWeight: 800, marginBottom: 4 },
  modalDesc: { fontSize: 13, color: "#64748B", marginBottom: 16 },
  form: { display: "flex", flexDirection: "column", gap: 14 },
  label: { fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 4, display: "block" },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
    width: "100%",
    backgroundColor: "#FFFFFF",
    color: "#0F172A",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
